import React, { useMemo, } from 'react';
import { useLocation, useHistory, } from 'react-router';
import { Button, UncontrolledTooltip, Nav, NavItem, NavLink, } from 'reactstrap';
import classnames from 'classnames';
import qs from 'qs';
import { keyBy, sortBy, } from 'lodash';
import { query, where, collection, doc, updateDoc, } from 'firebase/firestore';
import { format as formatDate, isValid as isValidDate, } from 'date-fns';
import { toast } from 'react-toastify';

import { fullPathWithParams, } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import TenantPage from '../TenantPage';
import QuerySelector from '../QuerySelector';
import ModelFormModal from '../modals/ModelFormModal';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import { orderDocId } from '../../shared/models/seller';
import {
  fields as ruleFields,
  timingOptions,
  asinModes,
  lowRatingThresholds,
  solicitationBaseDate,
} from '../../shared/models/reviewRequestRule';

// reviewRequest.status の表示定義。
const statusLabels = {
  pending: { label: '送信予定', className: 'bg-primary-subtle text-primary' },
  requested: { label: '送信済み', className: 'bg-success-subtle text-success' },
  skipped: { label: '対象外', className: 'bg-secondary-subtle text-secondary' },
  failed: { label: '失敗', className: 'bg-danger-subtle text-danger' },
  stopped: { label: '停止中', className: 'bg-warning-subtle text-warning' },
  excluded: { label: '除外', className: 'bg-secondary-subtle text-secondary' },
};

const skipReasonLabels = {
  no_available_action: '送信可能期間外',
  already_requested: '依頼済み',
  excluded_refunded: '返金済み',
  excluded_low_rating: '低評価',
};

const timingLabelByValue = keyBy(timingOptions, 'value');
const asinModeLabelByValue = keyBy(asinModes, 'value');
const lowRatingThresholdLabelByValue = keyBy(lowRatingThresholds, 'value');

// requestedAt は Firestore Timestamp、purchaseDate 等は SP-API の ISO 文字列で保存されるため、
// どちらでも Date に変換できるようにする。不正値は '-' 表示にして描画を落とさない。
const toDate = (value) => {
  const date = value?.toDate ? value.toDate() : new Date(value);
  return isValidDate(date) ? date : null;
};
const formatDateValue = (value) => { const date = value && toDate(value); return date ? formatDate(date, 'yyyy/MM/dd HH:mm') : '-'; };
const formatDay = (value) => { const date = value && toDate(value); return date ? formatDate(date, 'yyyy/MM/dd') : '-'; };

export default function ReviewRequests (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant, match, } = props;
  const location = useLocation();
  const history = useHistory();
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const sortedSellers = useMemo(() => sortBy(sellers, _ => _.index ?? _.name), [sellers]);
  // 選択セラー: URLクエリ(?sellerId=)優先、次にパスパラメータ(旧導線)、無ければ先頭セラー。
  // タブもURLクエリ(?tab=)で管理する。注文一覧は件数が多いため、orders タブを開いたときだけ購読する。
  const { sellerId: querySellerId, tab = 'rules' } = qs.parse(decodeURI(location.search.slice(1)));
  const sellerId = querySellerId || match.params.sellerId || sortedSellers[0]?.id;
  const sellerOptions = useMemo(() => sortedSellers.map(_ => ({ label: _.name || _.id, value: _.id })), [sortedSellers]);

  const rules = useCollectionSubscription(
    sellerId && query(collection(tenant.ref, 'reviewRequestRules'), where('sellerId', '==', sellerId)),
    [sellerId],
  );
  const orders = useCollectionSubscription(
    sellerId && tab === 'orders' && query(collection(tenant.ref, 'amazonOrders'), where('sellerId', '==', sellerId)),
    [sellerId, tab],
  );

  const sortedRules = useMemo(() => sortBy(rules, _ => _.createdAt), [rules]);
  const sortedOrders = useMemo(() => sortBy(orders, _ => _.purchaseDate).reverse(), [orders]);

  // 送信予定の注文を「停止中」にする(逆も可能)。停止中は送信バッチの対象から外れる。
  const toggleStop = async (order) => {
    const isStopped = order.reviewRequest?.status === 'stopped';
    try {
      await updateDoc(doc(collection(tenant.ref, 'amazonOrders'), orderDocId(sellerId, order.amazonOrderId)), {
        reviewRequest: isStopped
          ? { ...order.reviewRequest, status: 'pending', stoppedAt: null }
          : { ...order.reviewRequest, status: 'stopped', stoppedAt: new Date() },
      });
      toast.success(isStopped ? '送信を再開しました' : '送信を停止しました');
    } catch (e) {
      console.error(e);
      toast.error('更新に失敗しました');
    }
  };

  // ルール保存時に sellerId を注入する。
  const processRuleValues = (values) => ({ ...values, sellerId });

  // タブ切替はURLクエリ(?tab=)で管理する(state不使用)。
  const changeTab = (value) => history.replace(encodeURI(fullPathWithParams({ tab: value }, location)));

  return (
    <div className="container-fluid">
      <div className="px-4">
        <div className="card p-4">
          <div className="mb-3">
            <QuerySelector
              paramName="sellerId"
              label="セラー"
              options={sellerOptions}
              defaultValue={sellerId ? [sellerId] : []}
            />
          </div>

          <Nav tabs className="mb-3">
            {[
              { value: 'rules', label: '送信ルール' },
              { value: 'orders', label: '対象注文' },
            ].map(({ value, label }) => (
              <NavItem key={value}>
                <NavLink
                  className={classnames('cursor-pointer', { active: tab === value })}
                  onClick={() => changeTab(value)}
                >
                  {label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          {!sellerId && (
            <div className="alert alert-info mb-0">セラーが登録されていません。</div>
          )}

          {sellerId && tab === 'rules' && (
            <>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">
                  送信ルール
                  <span id="reviewRulesHelp" className="fas fa-question-circle text-muted ms-2" style={{ cursor: 'help', fontSize: '0.85rem' }} />
                  <UncontrolledTooltip target="reviewRulesHelp" style={{ maxWidth: 320 }}>
                    注文がいずれかの有効なルールに当てはまると、レビュー依頼の送信対象になります。実際の送信可否は最終的にAmazon側が判断します。
                  </UncontrolledTooltip>
                </h5>
                <AddButton
                  label="ルールを追加"
                  itemRef={_ => doc(collection(tenant.ref, 'reviewRequestRules'))}
                  FormModal={ModelFormModal}
                  formProps={{ title: 'ルールを追加', fields: ruleFields() }}
                  processValues={processRuleValues}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>ルール名</th>
                      <th>状態</th>
                      <th>送信タイミング</th>
                      <th>対象商品</th>
                      <th>除外条件</th>
                      <th className="text-end"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRules.map((rule) => {
                      const ref = doc(collection(tenant.ref, 'reviewRequestRules'), rule.id);
                      const excludes = [
                        rule.excludeRefunded && '返金',
                        rule.excludeLowRating && `低評価(${lowRatingThresholdLabelByValue[rule.lowRatingThreshold ?? 3]?.label || '星3以下'})`,
                      ].filter(Boolean);
                      return (
                        <tr key={rule.id}>
                          <td>{rule.name}</td>
                          <td>
                            {rule.enabled === false
                              ? <span className="badge bg-secondary-subtle text-secondary">無効</span>
                              : <span className="badge bg-success-subtle text-success">有効</span>}
                          </td>
                          <td className="small">{timingLabelByValue[rule.timingDays]?.label || `出荷の${rule.timingDays}日後`}</td>
                          <td className="small">
                            {asinModeLabelByValue[rule.asinMode]?.label || 'すべての商品'}
                            {rule.asinMode !== 'all' && rule.asins && <span className="text-muted">（{rule.asins}）</span>}
                          </td>
                          <td className="small">{excludes.length ? excludes.join(' / ') : '-'}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-1">
                              <EditButton
                                itemRef={ref}
                                FormModal={ModelFormModal}
                                formProps={{ title: 'ルールを編集', fields: ruleFields() }}
                                processValues={processRuleValues}
                              />
                              <DeleteButton itemRef={ref} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sortedRules.length === 0 && (
                <div className="text-muted text-center py-4">ルールがありません。「ルールを追加」から作成してください。</div>
              )}
            </>
          )}

          {sellerId && tab === 'orders' && (
            <>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>注文番号</th>
                      <th>注文日</th>
                      <th>配送状況</th>
                      <th>出荷予定日</th>
                      <th>依頼状況</th>
                      <th>依頼日時</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map((order) => {
                      const status = order.reviewRequest?.status || 'pending';
                      const statusDef = statusLabels[status] || statusLabels.pending;
                      const reason = order.reviewRequest?.reason;
                      const ruleName = order.reviewRequest?.ruleName;
                      const canStop = ['pending', 'stopped'].includes(status);
                      const baseDate = solicitationBaseDate(order);
                      return (
                        <tr key={order.amazonOrderId}>
                          <td className="small">{order.amazonOrderId}</td>
                          <td className="small">{formatDay(order.purchaseDate)}</td>
                          <td className="small">{order.orderStatus || '-'}</td>
                          <td className="small">{formatDay(baseDate)}</td>
                          <td>
                            <span className={`badge ${statusDef.className}`}>{statusDef.label}</span>
                            {reason && skipReasonLabels[reason] && (
                              <span className="text-muted small ms-1">({skipReasonLabels[reason]})</span>
                            )}
                            {ruleName && status === 'requested' && (
                              <span className="text-muted small ms-1">[{ruleName}]</span>
                            )}
                          </td>
                          <td className="small">{formatDateValue(order.reviewRequest?.requestedAt)}</td>
                          <td className="text-end">
                            {canStop && (
                              <Button color="link" className="p-0 small" onClick={() => toggleStop(order)}>
                                {status === 'stopped' ? '送信を再開' : '送信を停止'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sortedOrders.length === 0 && (
                <div className="text-muted text-center py-4">対象の注文がありません</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
